import { ChangeEvent, FC, useEffect } from "react";
import { Controller } from "react-hook-form";
import { useI18n } from "../../hooks/useTranslations";
import { ProcessingType, ProcessingTypeViews } from "../../http/processingType/type";
import { formatViewName } from "../../utils";
import CheckBox from "../ui/CheckBox";
import Form from "../ui/Form";
import FormField from "../ui/FormField";
import Inline from "../ui/Inline";
import Input from "../ui/Input";
import MultiCheckbox from "../ui/MultiCheckbox";

export interface ProcessingTypeFormBody {
  id: string;
  name: string;
  showMarketingFields: boolean;
  // isKitRequest: boolean;
  allowQuantityVariation: boolean;
  initialItemIsFinalItem: boolean;
  views: string[];
}

type ProcessingTypeFormProps = {
  processingType?: ProcessingType;
  onSubmit: (data: ProcessingTypeFormBody) => void;
  isDisabled?: boolean;
  isLoading?: boolean;
};

const ProcessingTypeForm: FC<ProcessingTypeFormProps> = ({ processingType, onSubmit, isDisabled, isLoading }) => {
  const i18n = useI18n();

  const defaultValues = {
    name: processingType?.name ?? "",
    showMarketingFields: !!processingType?.showMarketingFields,
    // isKitRequest: !!processingType?.isKitRequest,
    allowQuantityVariation: !!processingType?.allowQuantityVariation,
    initialItemIsFinalItem: !!processingType?.initialItemIsFinalItem,
    views: processingType?.views ?? ["all-requests"],
  };

  return (
    <Form<ProcessingTypeFormBody>
      defaultValues={defaultValues}
      submitText={i18n.t.common.save}
      disabled={isDisabled}
      onSubmit={() => onSubmit}
    >
      {({ register, control, formState: { errors }, reset, setValue, watch }) => {
        const views = watch("views");

        useEffect(() => {
          reset(defaultValues);
        }, [processingType]);

        return (
          <>
            <FormField label={i18n.t.common.name} error={errors.name}>
              {({ labelId, isInvalid }) => (
                <Input
                  id={labelId}
                  {...register("name", {
                    required: true,
                  })}
                  isInvalid={isInvalid}
                  disabled={isDisabled || isLoading}
                />
              )}
            </FormField>

            <Controller
              name="showMarketingFields"
              control={control}
              render={({ field }) => (
                <Inline alignItems="center" columnGap={1}>
                  <FormField label={i18n.t.common.showsMarketingFields + ":"} children={undefined}></FormField>
                  <CheckBox
                    checked={field.value}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => field.onChange(e.target.checked)}
                    disabled={isDisabled || isLoading}
                  />
                </Inline>
              )}
            />

            <Controller
              name="allowQuantityVariation"
              control={control}
              render={({ field }) => (
                <Inline alignItems="center" columnGap={1}>
                  <FormField label={i18n.t.common.allowQuantityVariation + ":"} children={undefined}></FormField>
                  <CheckBox
                    checked={field.value}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => field.onChange(e.target.checked)}
                    disabled={isDisabled || isLoading}
                  />
                </Inline>
              )}
            />

            <Controller
              name="initialItemIsFinalItem"
              control={control}
              render={({ field }) => (
                <Inline alignItems="center" columnGap={1}>
                  <FormField label={i18n.t.common.initialItemIsFinalItem + ":"} children={undefined}></FormField>
                  <CheckBox
                    checked={field.value}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => field.onChange(e.target.checked)}
                    disabled={isDisabled || isLoading}
                  />
                </Inline>
              )}
            />

            <FormField label={i18n.t.common.views}>
              <MultiCheckbox
                values={[
                  {
                    id: ProcessingTypeViews.Helmets,
                    title: formatViewName(ProcessingTypeViews.Helmets, i18n),
                    selected: views.includes("helmets"),
                  },
                  {
                    id: ProcessingTypeViews.AllRequests,
                    title: formatViewName(ProcessingTypeViews.AllRequests, i18n),
                    selected: views.includes("all-requests"),
                  },
                ]}
                disabled={isDisabled || isLoading}
                onUpdate={(name, checked) => {
                  if (checked && !views.includes(name)) {
                    setValue("views", [...views, name]);
                  }
                  if (!checked && views.includes(name)) {
                    setValue(
                      "views",
                      views.filter((v) => v !== name),
                    );
                  }
                }}
              />
            </FormField>
          </>
        );
      }}
    </Form>
  );
};

export default ProcessingTypeForm;
